import { PatientLookupErrorCode } from "@/shared";
import { logger, extractErrorDetails } from "@/src/lib/logging/logger";
import type { Classification } from "@/src/models/constants/classifications";

type ErrorMeta = Record<string, unknown>;

class BaseError extends Error {
  constructor(
    message: string,
    public readonly meta?: ErrorMeta
  ) {
    super(message);
  }
}

export class NotFoundError extends BaseError {}
export class PatientNotFoundError extends NotFoundError {}
export class BadRequestError extends BaseError {}
export class PatientMatchAmbiguousError extends BadRequestError {
  constructor(message = "Multiple patients found") {
    super(message, { code: PatientLookupErrorCode.MatchAmbiguous });
  }
}
export class InternalServerError extends BaseError {}
export class UnauthorizedError extends BaseError {}
export class RequestTimeoutError extends BaseError {}
export class UnprocessableEntityError extends BaseError {}
export class ServiceUnavailableError extends BaseError {
  constructor(
    message: string,
    public readonly code?: string,
    meta?: ErrorMeta
  ) {
    super(message, meta);
  }
}
/** The extracted payer/plan didn't match one of the EHR's insurance options. */
export class AmbiguousInsuranceMappingError extends BaseError {
  constructor(
    public readonly payerText: string,
    meta?: ErrorMeta
  ) {
    super(`Payer "${payerText}" did not match an EHR insurance option`, meta);
  }
}
/** The section's upload classification has no document-type mapping in the EHR's tenant config. */
export class UnmappedDocumentTypeError extends BaseError {
  constructor(
    public readonly classification: Classification,
    meta?: ErrorMeta
  ) {
    super(`No EHR document type is configured for classification "${classification}"`, meta);
  }
}
// Deliberately not mapped in handleApiError: worker call sites keep failing closed (500 → Cloud
// Tasks retry); agent endpoints translate it to a 422 themselves.
export class MissingMrnError extends BaseError {}
export class PatientNotFoundInEhrError extends MissingMrnError {}
export class ConflictError extends Error {
  public readonly code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

/**
 * Extracts an error message from an unknown error type.
 * Supports standard Error instances, custom objects with a message string property,
 * or falls back to the specified string (or stringified error).
 * @param error - The error to extract a message from
 * @param fallback - Optional fallback message
 * @returns The error message or fallback
 */
export function getErrorMessage(error: unknown, fallback?: string): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === "object" && "message" in error && typeof error.message === "string") {
    return error.message;
  }

  return fallback !== undefined ? fallback : String(error);
}

export function handleApiError(
  error: unknown,
  meta?: Record<string, string | number | boolean | undefined | null>,
  customLogger?: typeof logger
): Response {
  const errorDetails = extractErrorDetails(error);
  const log = customLogger ?? logger;

  const errorMeta = error instanceof BaseError ? error.meta : undefined;

  if (error instanceof BadRequestError) {
    log.warn("Bad request error", { success: false, error: errorDetails, ...errorMeta, ...meta });

    const code = typeof error.meta?.code === "string" ? error.meta.code : undefined;

    return Response.json({ error: errorDetails.error, ...(code ? { code } : {}) }, { status: 400 });
  }

  if (error instanceof PatientNotFoundError) {
    log.warn("Patient not found error", { success: false, error: errorDetails, ...errorMeta, ...meta });

    return Response.json({ error: error.message, code: PatientLookupErrorCode.NotFound }, { status: 404 });
  }

  if (error instanceof NotFoundError) {
    log.warn("Not found error", { success: false, error: errorDetails, ...errorMeta, ...meta });

    return Response.json({ error: errorDetails.error }, { status: 404 });
  }

  if (error instanceof ConflictError) {
    log.warn("Conflict error", { success: false, error: errorDetails, code: error.code, ...meta });

    return Response.json({ error: errorDetails.error, code: error.code }, { status: 409 });
  }

  if (error instanceof UnprocessableEntityError) {
    log.warn("Unprocessable entity error", { success: false, error: errorDetails, ...errorMeta, ...meta });

    return Response.json({ error: errorDetails.error }, { status: 422 });
  }

  if (error instanceof UnauthorizedError) {
    log.error("Unauthorized error", { success: false, error: errorDetails, ...errorMeta, ...meta });

    return Response.json({ error: errorDetails.error }, { status: 403 });
  }

  if (error instanceof InternalServerError) {
    log.error("Internal server error", { success: false, error: errorDetails, ...errorMeta, ...meta });

    return Response.json({ error: errorDetails.error }, { status: 500 });
  }

  if (error instanceof ServiceUnavailableError) {
    log.error("Service unavailable error", {
      success: false,
      error: errorDetails,
      code: error.code,
      ...errorMeta,
      ...meta,
    });

    return Response.json({ error: error.message, ...(error.code ? { code: error.code } : {}) }, { status: 503 });
  }

  // Wrap unexpected errors as InternalServerError
  log.error("Unexpected error", { success: false, error: errorDetails, ...errorMeta, ...meta });

  return Response.json({ error: errorDetails.error }, { status: 500 });
}

// Error boundary for caller-facing endpoints where a missing MRN is a data condition (422), not a
// server fault. Worker call sites keep using plain handleApiError so Cloud Tasks retry. See VAL-2006.
export function handleMissingMrnApiError(
  error: unknown,
  meta?: Record<string, string | number | boolean | undefined | null>,
  customLogger?: typeof logger
): Response {
  if (error instanceof MissingMrnError) {
    // Keep the original as `cause` so the 422 log retains the throw-site stack (extractErrorDetails
    // formats the cause chain).
    const unprocessable = new UnprocessableEntityError(error.message, error.meta);

    unprocessable.cause = error;

    return handleApiError(unprocessable, meta, customLogger);
  }

  return handleApiError(error, meta, customLogger);
}

export function handleAgentApiError(
  error: unknown,
  meta?: Record<string, string | number | boolean | undefined | null>,
  customLogger?: typeof logger
): Response {
  return handleMissingMrnApiError(error, meta, customLogger);
}
